import { useState, useEffect, useRef, useMemo } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import {
    Box,
    Button,
    Typography,
    Paper,
    Modal,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Alert,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import jwt_decode from 'jwt-decode';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { PreviewSOA } from './PreviewSOA';
import { ApprovalStatuses } from '../Generate/Components/ApprovalStatuses';
import { SendSOAToTenant } from '../Generate/Components/SendSOAToTenant';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Progress } from 'reactstrap';
import { approveSOAWithJwt, rejectSOAWithJwt } from '../api';
import { SignatureCreator } from '../../NewMasterSheet/LeaseProcess/Flow/StepperComponents/lease/components/steps/components';
import Swal from 'sweetalert2';
import './soa.scss';

const customTheme = createTheme({
    typography: {
        fontFamily: '"Times New Roman", Times, serif',
        h6: { fontFamily: '"Times New Roman", Times, serif', fontWeight: 700 },
        body1: { fontFamily: '"Times New Roman", Times, serif', fontWeight: 400 },
        body2: { fontFamily: '"Times New Roman", Times, serif', fontWeight: 400 },
        button: { fontFamily: '"Times New Roman", Times, serif', fontWeight: 700 },
    },
});

const SOAListTable = ({ items, onDelete, scrollThreshold = 768, onRefresh }) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [selectedSOA, setSelectedSOA] = useState(null);
    const [showProgress, setShowProgress] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const soaFormRef = useRef(null);
    const sizePerPage = 10;

    const [currentRole, setCurrentRole] = useState('');
    const [currentUserEmail, setCurrentUserEmail] = useState('');

    useEffect(() => {
        const token = window.localStorage.getItem('accessToken');
        if (token) {
            try {
                const decoded = jwt_decode(token);
                setCurrentRole(decoded.role || '');
                setCurrentUserEmail(decoded.email || '');
            } catch (e) {
                console.error('Failed to decode JWT', e);
            }
        }
    }, []);

    // Signature & Reject Dialogs
    const [isSignatureOpen, setIsSignatureOpen] = useState(false);
    const [signatureTargetId, setSignatureTargetId] = useState('');
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState('');
    const [rejectionComments, setRejectionComments] = useState('');
    const [approvingId, setApprovingId] = useState('');
    const [rejectingId, setRejectingId] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Totals
    const totalSDIEMO = useMemo(() =>
        items.reduce((sum, row) => {
            const cell = row.account_details || {};
            return sum + ((cell.security || 0) + (cell.interest || 0) + (cell.early_move_out_prorate || 0));
        }, 0),
        [items]
    );

    const totalFinal = useMemo(() =>
        items.reduce((sum, row) => {
            const cell = row.account_details || {};
            const security = cell.security || 0;
            const interest = cell.interest || 0;
            const prorate = cell.early_move_out_prorate || 0;
            const deductions = cell.deductions || [];
            const totalDeductions = deductions.reduce((s, d) => s + (d.cost_per_action || 0) * (d.number_of_actions || 0), 0);
            return sum + (security + interest + prorate - totalDeductions);
        }, 0),
        [items]
    );

    // Handlers
    const handleRowClick = (row) => {
        setSelectedSOA(row);
        setIsPreviewModalOpen(true);
    };

    const handlePreviewClick = (row, e) => {
        e.stopPropagation();
        setSelectedSOA(row);
        setIsPreviewModalOpen(true);
    };

    const handleDeleteClick = (row, e) => {
        e.stopPropagation();
        onDelete(row);
    };

    const getLastApprovalDate = (row) => {
        const auditorApproval = row.auditor_approval || {};
        if (auditorApproval.updated_at) {
            return new Date(auditorApproval.updated_at).toLocaleDateString();
        }

        const approvals = row.approvals || [];
        if (approvals.length > 0) {
            const latestApproval = approvals.reduce((latest, approval) => {
                if (!approval.updated_at) return latest;
                return !latest || new Date(approval.updated_at) > new Date(latest.updated_at) ? approval : latest;
            }, null);
            return latestApproval?.updated_at ? new Date(latestApproval.updated_at).toLocaleDateString() : 'N/A';
        }

        return 'N/A';
    };

    const getApprovalStatus = (row) => {
        const approvals = row.approvals || [];
        const auditorApproval = row.auditor_approval || {};
        const hasRejection =
            approvals.some((approval) => approval.status === 'rejected') ||
            auditorApproval.status === 'rejected';
        const allApprovalsDone =
            approvals.length > 0 && approvals.every((approval) => approval.status === 'approved');

        if (hasRejection) {
            return { text: 'Rejected', color: 'error.main' };
        } else if (allApprovalsDone && auditorApproval.status === 'approved') {
            return { text: 'Approved', color: 'success.main' };
        } else if (allApprovalsDone) {
            return { text: 'Awaiting Auditor', color: 'info.main' };
        } else {
            return { text: 'Pending', color: 'warning.main' };
        }
    };

    // PDF Generation
    const generatePDFFile = async (showProgressUpdates = false) => {
        if (!soaFormRef.current) return null;

        try {
            if (showProgressUpdates) {
                setShowProgress(true);
                setProgressMessage('Preparing document...');
                setProgress(5);
            }

            const pdf = new jsPDF('p', 'mm', 'a4');
            const soaElement = soaFormRef.current;

            if (showProgressUpdates) {
                setProgressMessage('Capturing document...');
                setProgress(25);
            }

            const canvas = await html2canvas(soaElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true,
                backgroundColor: '#ffffff',
                removeContainer: true,
                imageTimeout: 15000,
                height: soaElement.scrollHeight,
                width: soaElement.scrollWidth,
            });

            if (showProgressUpdates) {
                setProgressMessage('Processing image...');
                setProgress(50);
            }

            const imgData = canvas.toDataURL('image/jpeg', 0.9);
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const sidePadding = 10;
            const topBottomPadding = 5;
            const availableWidth = pdfWidth - (sidePadding * 2);
            const availableHeight = pdfHeight - (topBottomPadding * 2);
            const imgWidth = imgProps.width;
            const imgHeight = imgProps.height;
            const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            const xOffset = sidePadding + (availableWidth - scaledWidth) / 2;
            const yOffset = topBottomPadding;

            if (showProgressUpdates) {
                setProgressMessage('Adding to PDF...');
                setProgress(75);
            }

            const pageHeight = availableHeight;
            const totalPages = Math.ceil(scaledHeight / pageHeight);

            for (let i = 0; i < totalPages; i++) {
                if (i > 0) pdf.addPage();
                const sourceY = i * (pageHeight / ratio);
                const sourceHeight = Math.min(pageHeight / ratio, imgHeight - sourceY);

                if (sourceHeight > 0) {
                    const pageCanvas = document.createElement('canvas');
                    const pageCtx = pageCanvas.getContext('2d');
                    const img = new Image();
                    await new Promise((resolve) => {
                        img.onload = () => {
                            pageCanvas.width = imgWidth;
                            pageCanvas.height = sourceHeight;
                            pageCtx.drawImage(img, 0, -sourceY);
                            const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.9);
                            pdf.addImage(pageImgData, 'JPEG', xOffset, yOffset, scaledWidth, sourceHeight * ratio);
                            resolve();
                        };
                        img.src = imgData;
                    });
                }
            }

            if (showProgressUpdates) {
                setProgressMessage('Finalizing PDF...');
                setProgress(90);
            }

            const pdfBlob = pdf.output('blob');
            const pdfFile = new File([pdfBlob], 'SOA-Statement.pdf', { type: 'application/pdf' });

            if (showProgressUpdates) {
                setProgress(100);
                setProgressMessage('PDF generated successfully!');
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            return pdfFile;
        } catch (error) {
            console.error('Error generating PDF:', error);
            if (showProgressUpdates) setProgressMessage('Error generating PDF.');
            throw error;
        } finally {
            if (showProgressUpdates) {
                setTimeout(() => {
                    setShowProgress(false);
                    setProgress(0);
                    setProgressMessage('');
                }, 1000);
            }
        }
    };

    const handlePrint = async () => {
        try {
            const pdfFile = await generatePDFFile(true);
            if (pdfFile) {
                const url = URL.createObjectURL(pdfFile);
                const printWindow = window.open(url, '_blank');
                if (printWindow) {
                    printWindow.onload = () => {
                        printWindow.print();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                    };
                } else {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'SOA-Statement.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }
            }
        } catch (error) {
            console.error('Print failed:', error);
            alert('Failed to generate PDF for printing. Please try again.');
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const pdfFile = await generatePDFFile(true);
            if (pdfFile) {
                const url = URL.createObjectURL(pdfFile);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'SOA-Statement.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to generate PDF for download. Please try again.');
        }
    };

    // Approve with JWT
    const handleApproveWithJwt = async ({ signatureData }) => {
        if (!signatureTargetId) return;
        setApprovingId(signatureTargetId);
        try {
            const response = await approveSOAWithJwt(signatureTargetId, { signature: signatureData });
            if (response.success) {
                setSnackbar({ open: true, message: 'Approved successfully!', severity: 'success' });
                onRefresh?.();
            } else {
                throw new Error(response.message || 'Approval failed');
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'Failed to approve', severity: 'error' });
        } finally {
            setApprovingId('');
            setIsSignatureOpen(false);
            setSignatureTargetId('');
        }
    };

    const openSignature = (id) => {
        setSignatureTargetId(id);
        setIsSignatureOpen(true);
    };

    // Reject with JWT
    const handleRejectConfirm = async () => {
        if (!rejectTargetId) return;
        setRejectingId(rejectTargetId);
        try {
            const response = await rejectSOAWithJwt(rejectTargetId, { comments: rejectionComments });
            if (response.success) {
                setSnackbar({ open: true, message: 'Rejected successfully.', severity: 'warning' });
                onRefresh?.();
                await Swal.fire('Rejected', 'SOA has been rejected.', 'success');
            } else {
                throw new Error(response.message || 'Rejection failed');
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.message || 'Failed to reject', severity: 'error' });
        } finally {
            setRejectingId('');
            setRejectDialogOpen(false);
            setRejectionComments('');
            setRejectTargetId('');
        }
    };

    const openReject = (id) => {
        setRejectTargetId(id);
        setRejectionComments('');
        setRejectDialogOpen(true);
    };

    // Approval Helpers
    const userHasPendingApproval = (row) => {
        return row.approvals?.some(
            (a) => a.email?.toLowerCase() === currentUserEmail?.toLowerCase() && a.status === 'pending'
        );
    };

    const userHasApproved = (row) => {
        return row.approvals?.some(
            (a) => a.email?.toLowerCase() === currentUserEmail?.toLowerCase() && a.status === 'approved'
        );
    };

    const userHasRejected = (row) => {
        return row.approvals?.some(
            (a) => a.email?.toLowerCase() === currentUserEmail?.toLowerCase() && a.status === 'rejected'
        );
    };

    const isAllowedToApprove = ['company', 'technical staff'].includes(currentRole);

    // Columns
    const columns = [
        {
            dataField: 'serialNo',
            text: 'S.No',
            sort: true,
            footer: '',
            footerFormatter: () => (
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: 12 }}>
                    Total
                </Typography>
            ),
            formatter: (cell, row, rowIndex) => ((currentPage - 1) * sizePerPage) + (rowIndex + 1),
        },
        {
            dataField: 'tenant.0.name',
            text: 'Tenant Name',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell || 'N/A'} arrow>
                    <span>{cell || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'property_details.unit_no',
            text: 'Unit No',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell || 'N/A'} arrow>
                    <span>{cell || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'createdAt',
            text: 'Date Created On',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => {
                const date = cell ? new Date(cell).toLocaleDateString() : 'N/A';
                return (
                    <Tooltip title={date} arrow>
                        <span>{date}</span>
                    </Tooltip>
                );
            },
        },
        {
            dataField: 'account_details',
            text: 'Total Amt (SD+I+EMO)',
            sort: true,
            footer: 'Total SD+I+EMO',
            footerFormatter: () => `$${Number(totalSDIEMO).toLocaleString()}`,
            formatter: (cell) => {
                const security = cell?.security || 0;
                const interest = cell?.interest || 0;
                const prorate = cell?.early_move_out_prorate || 0;
                const total = security + interest + prorate;
                const totalFormatted = `$${Number(total).toLocaleString()}`;
                return (
                    <Tooltip title={`$${security} + $${interest} + $${prorate} = ${totalFormatted}`} arrow>
                        <span>{totalFormatted}</span>
                    </Tooltip>
                );
            },
        },
        {
            dataField: 'account_details',
            text: 'Final Amount',
            sort: true,
            footer: 'Total Final Amount',
            footerFormatter: () => {
                const displayValue = Math.abs(totalFinal).toLocaleString();
                const suffix = totalFinal < 0 ? ' (Deducted)' : ' (Refunded)';
                const color = totalFinal < 0 ? 'error.main' : 'success.main';
                return (
                    <Typography variant="body2" sx={{ color, fontWeight: 'medium', fontSize: 12 }}>
                        ${displayValue}{suffix}
                    </Typography>
                );
            },
            formatter: (cell) => {
                const security = cell?.security || 0;
                const interest = cell?.interest || 0;
                const prorate = cell?.early_move_out_prorate || 0;
                const totalDeductions = cell?.deductions
                    ? cell.deductions.reduce((sum, d) => sum + (d.cost_per_action || 0) * (d.number_of_actions || 0), 0)
                    : 0;
                const finalAmount = security + interest + prorate - totalDeductions;
                return (
                    <Typography variant="body2" sx={{ color: finalAmount < 0 ? 'error.main' : 'success.main', fontWeight: 'medium', fontSize: 12 }}>
                        ${Math.abs(finalAmount).toLocaleString()}
                        {finalAmount < 0 ? ' (Deducted)' : ' (Refunded)'}
                    </Typography>
                );
            },
        },
        {
            dataField: 'email_details.emailed_at',
            text: 'Mailed On',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell ? new Date(cell).toLocaleDateString() : 'N/A'} arrow>
                    <span>{cell ? new Date(cell).toLocaleDateString() : 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'landlord_details.name',
            text: 'Landlord',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell) => (
                <Tooltip title={cell || 'N/A'} arrow>
                    <span>{cell || 'N/A'}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'last_approval_date',
            text: 'Last Approval',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell, row) => (
                <Tooltip title={getLastApprovalDate(row)} arrow>
                    <span>{getLastApprovalDate(row)}</span>
                </Tooltip>
            ),
        },
        {
            dataField: 'approvals',
            text: 'Approval Status',
            sort: true,
            footer: '',
            footerFormatter: () => '',
            formatter: (cell, row) => {
                const status = getApprovalStatus(row);
                return (
                    <Typography
                        variant="body2"
                        sx={{
                            color: status.color,
                            fontWeight: 'medium',
                            fontSize: 12,
                            bgcolor: status.color + '.light',
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            display: 'inline-block',
                        }}
                    >
                        {status.text}
                    </Typography>
                );
            },
        },
        {
            dataField: 'approve_jwt',
            text: 'Approve',
            headerStyle: { width: '110px', textAlign: 'center' },
            style: { textAlign: 'center' },
            formatter: (cell, row) => {
                if (!isAllowedToApprove || !currentUserEmail) return null;

                const isPending = userHasPendingApproval(row);
                const alreadyApproved = userHasApproved(row);
                const alreadyRejected = userHasRejected(row);

                if (!isPending || alreadyApproved || alreadyRejected) {
                    return (
                        <Typography variant="caption" color="text.secondary">
                            {alreadyApproved ? 'Approved' : alreadyRejected ? 'Rejected' : 'N/A'}
                        </Typography>
                    );
                }

                return (
                    <Tooltip title="Approve with your signature">
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={approvingId === row._id}
                            onClick={(e) => {
                                e.stopPropagation();
                                openSignature(row._id);
                            }}
                            sx={{ minWidth: 85, fontSize: 11 }}
                        >
                            {approvingId === row._id ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                <>
                                    <FeatherIcon icon="check" size={14} />
                                    Approve
                                </>
                            )}
                        </Button>
                    </Tooltip>
                );
            },
        },
        {
            dataField: 'reject_jwt',
            text: 'Reject',
            headerStyle: { width: '110px', textAlign: 'center' },
            style: { textAlign: 'center' },
            formatter: (cell, row) => {
                if (!isAllowedToApprove || !currentUserEmail) return null;

                const isPending = userHasPendingApproval(row);
                const alreadyApproved = userHasApproved(row);
                const alreadyRejected = userHasRejected(row);

                if (!isPending || alreadyApproved || alreadyRejected) {
                    return (
                        <Typography variant="caption" color="text.secondary">
                            {alreadyRejected ? 'Rejected' : alreadyApproved ? 'Approved' : 'N/A'}
                        </Typography>
                    );
                }

                return (
                    <Tooltip title="Reject with optional comment">
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={rejectingId === row._id}
                            onClick={(e) => {
                                e.stopPropagation();
                                openReject(row._id);
                            }}
                            sx={{ minWidth: 85, fontSize: 11 }}
                        >
                            {rejectingId === row._id ? (
                                <span className="spinner-border spinner-border-sm" />
                            ) : (
                                <>
                                    <FeatherIcon icon="x" size={14} />
                                    Reject
                                </>
                            )}
                        </Button>
                    </Tooltip>
                );
            },
        },
        // Actions
        {
            dataField: 'actions',
            text: 'Actions',
            footer: '',
            footerFormatter: () => '',
            formatter: (cell, row) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <Tooltip title="Preview" arrow>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => handlePreviewClick(row, e)}
                            sx={{
                                bgcolor: 'info.main',
                                color: 'white',
                                minWidth: '40px',
                                p: 0.5,
                                '&:hover': { bgcolor: 'info.dark' },
                            }}
                        >
                            <FeatherIcon icon="eye" size={16} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => handleDeleteClick(row, e)}
                            sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                minWidth: '40px',
                                p: 0.5,
                                '&:hover': { bgcolor: 'error.dark' },
                            }}
                        >
                            <FeatherIcon icon="trash-2" size={16} />
                        </Button>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    const paginationOptions = {
        sizePerPage,
        hideSizePerPage: true,
        hidePageListOnlyOnePage: true,
        onPageChange: (page) => setCurrentPage(page),
    };

    const NoDataIndication = () => (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh" width="100%">
            <Box textAlign="center" py={4}>
                <FeatherIcon icon="file-text" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <Typography variant="h6" color="text.secondary">
                    No Statements of Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No SOAs available for preview
                </Typography>
            </Box>
        </Box>
    );

    const shouldShowVerticalScrollbar = windowWidth <= scrollThreshold;

    const tableStyles = {
        minHeight: '50vh',
        maxHeight: '75vh',
        overflowY: shouldShowVerticalScrollbar ? 'auto' : 'visible',
        overflowX: 'auto',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        bgcolor: 'background.paper',
        width: 'calc(100% - 30px)',
        minWidth: '600px',
        position: 'relative',
        '& .react-bootstrap-table': { minHeight: '50vh' },
        '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            width: '100%',
            bgcolor: 'background.paper',
        },
        '& thead th': {
            position: 'sticky',
            top: 0,
            backgroundColor: 'grey.100',
            zIndex: 2,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            padding: '14px 10px',
            fontWeight: '600',
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        },
        '& tbody td': {
            padding: '14px 10px',
            borderBottom: '1px solid',
            borderColor: 'grey.100',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'text.secondary',
            fontSize: 12,
            verticalAlign: 'middle',
        },
        '& tbody tr': {
            transition: 'background-color 0.2s ease',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: 'grey.50',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            },
        },
        '& .pagination': {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '12px 16px',
            borderTop: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'grey.100',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            '& .page-link': {
                borderRadius: '4px',
                margin: '0 4px',
                color: 'primary.main',
                '&:hover': { backgroundColor: 'primary.light', color: 'primary.dark' },
            },
            '& .page-item.active .page-link': {
                backgroundColor: 'primary.main',
                color: 'white',
                borderColor: 'primary.main',
            },
        },
        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
        '&::-webkit-scrollbar-track': { background: 'grey.100', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb': {
            background: 'grey.400',
            borderRadius: '4px',
            '&:hover': { background: 'grey.600' },
        },
        'scrollbar-width': 'thin',
        'scrollbar-color': 'grey.400 grey.100',
    };

    return (
        <>
            <Paper sx={tableStyles} className="soa-table-container miui-table scrollable-table">
                <BootstrapTable
                    keyField="_id"
                    data={items || []}
                    columns={columns}
                    pagination={paginationFactory(paginationOptions)}
                    bootstrap4
                    bordered={false}
                    hover
                    striped
                    noDataIndication={NoDataIndication}
                    footer={true}
                    classes="px-2"
                    rowEvents={{ onClick: (e, row) => handleRowClick(row) }}
                    rowStyle={{ cursor: 'pointer' }}
                />
            </Paper>

            {/* Preview Modal */}
            <Modal open={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        p: 4,
                        borderRadius: '8px',
                        maxWidth: '1240px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                    }}
                >
                    {showProgress && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 9999,
                            }}
                        >
                            <Paper elevation={3} sx={{ p: 4, minWidth: 300, textAlign: 'center' }}>
                                <Box className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" sx={{ mb: 2 }} />
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    {progressMessage}
                                </Typography>
                                <Box sx={{ width: '100%', mb: 2 }}>
                                    <Progress value={progress} color="primary" />
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {progress}% Complete
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                    <Typography id="preview-modal-title" variant="h6" component="h2" mb={2}>
                        Preview Statement of Account
                    </Typography>
                    <ThemeProvider theme={customTheme}>
                        <div ref={soaFormRef}>
                            <PreviewSOA soaId={selectedSOA?._id} showInvoices={true} showDownloadButtons={false} showAttachments={false} />
                        </div>
                        {selectedSOA && <ApprovalStatuses soaId={selectedSOA._id} soaData={selectedSOA} />}
                        {selectedSOA && <SendSOAToTenant soaId={selectedSOA._id} soaData={selectedSOA} />}
                    </ThemeProvider>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Tooltip title="Delete" arrow>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => handleDeleteClick(selectedSOA, e)}
                                sx={{
                                    bgcolor: 'error.main',
                                    color: 'white',
                                    minWidth: '100px',
                                    p: 1,
                                    '&:hover': { bgcolor: 'error.dark' },
                                }}
                            >
                                <FeatherIcon icon="trash-2" size={18} style={{ marginRight: 8 }} />
                                Delete
                            </Button>
                        </Tooltip>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePrint}
                            disabled={showProgress}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1, '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            <FeatherIcon icon="printer" size={18} />
                            Print SOA
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleDownloadPDF}
                            disabled={showProgress}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1, '&:hover': { bgcolor: 'success.dark' } }}
                        >
                            <FeatherIcon icon="download" size={18} />
                            Download PDF
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setIsPreviewModalOpen(false)}
                            disabled={showProgress}
                            sx={{ borderColor: 'grey.400', color: 'grey.700', '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' } }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Signature Creator */}
            <SignatureCreator
                isOpen={isSignatureOpen}
                onClose={() => {
                    setIsSignatureOpen(false);
                    setSignatureTargetId('');
                }}
                onSign={handleApproveWithJwt}
                onSignAndSave={handleApproveWithJwt}
                initialSignature=""
                width={600}
                height={200}
                disableSignAndSave={true}
            />

            {/* Reject Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject Statement of Account</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Are you sure you want to reject this SOA?
                    </Typography>
                    <TextField
                        label="Comments (Optional)"
                        multiline
                        rows={3}
                        fullWidth
                        value={rejectionComments}
                        onChange={(e) => setRejectionComments(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleRejectConfirm}
                        color="error"
                        variant="contained"
                        disabled={rejectingId}
                    >
                        {rejectingId ? 'Rejecting...' : 'Yes, Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export { SOAListTable };